import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { AuthContext } from './AuthContext';

const WebSocketContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_RECONNECT_ATTEMPTS = 3;

function getWsUrl(path, token) {
  if (!BACKEND_URL || !token) return null;
  const base = BACKEND_URL.replace(/^http/, 'ws');
  return `${base}${path}?token=${encodeURIComponent(token)}`;
}

export function WebSocketProvider({ children }) {
  const { user } = useContext(AuthContext);
  const userToken = user?.token;
  const tradingWsRef = useRef(null);
  const chartsWsRef = useRef(null);
  const [tradingReady, setTradingReady] = useState(false);
  const [chartsReady, setChartsReady] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const reconnectAttemptsRef = useRef({ trading: 0, charts: 0 });
  const reconnectTimersRef = useRef({ trading: null, charts: null });

  const tradingListenersRef = useRef(new Set());
  const chartsListenersRef = useRef(new Set());

  const connect = useCallback((path, wsRef, setReady, socketKey, listenersRef) => {
    const token = userToken || (typeof window !== 'undefined' && window.__authToken);
    const url = getWsUrl(path, token);
    if (!url) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        const handler = (e) => {
          try {
            const msg = JSON.parse(e.data);
            listenersRef.current.forEach((fn) => fn(msg));
          } catch (_) {}
        };
        ws._messageHandler = handler;
        ws.addEventListener('message', handler);
        setReady(true);
        setConnectionLost(false);
        reconnectAttemptsRef.current[socketKey] = 0;
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        setReady(false);
        if (ws._messageHandler) {
          try { ws.removeEventListener('message', ws._messageHandler); } catch (_) {}
          ws._messageHandler = null;
        }
        wsRef.current = null;

        if (reconnectAttemptsRef.current[socketKey] >= MAX_RECONNECT_ATTEMPTS) {
          setConnectionLost(true);
          return;
        }

        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current[socketKey]), 30000);
        reconnectAttemptsRef.current[socketKey]++;
        reconnectTimersRef.current[socketKey] = setTimeout(() => {
          connect(path, wsRef, setReady, socketKey, listenersRef);
        }, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (err) {
      console.warn('[WebSocket] Connect failed:', err);
    }
  }, [userToken]);

  useEffect(() => {
    const token = userToken;
    if (!token) {
      setTradingReady(false);
      setChartsReady(false);
      return;
    }

    connect('/ws/trading', tradingWsRef, setTradingReady, 'trading', tradingListenersRef);
    connect('/ws/charts', chartsWsRef, setChartsReady, 'charts', chartsListenersRef);

    return () => {
      if (reconnectTimersRef.current.trading) clearTimeout(reconnectTimersRef.current.trading);
      if (reconnectTimersRef.current.charts) clearTimeout(reconnectTimersRef.current.charts);
      reconnectTimersRef.current = { trading: null, charts: null };
      tradingWsRef.current?.close();
      chartsWsRef.current?.close();
      tradingWsRef.current = null;
      chartsWsRef.current = null;
      setTradingReady(false);
      setChartsReady(false);
    };
  }, [userToken, connect]);

  useEffect(() => {
    const onUnload = () => {
      tradingWsRef.current?.close();
      chartsWsRef.current?.close();
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('pagehide', onUnload);
    };
  }, []);

  const sendTrading = useCallback((msg) => {
    if (tradingWsRef.current?.readyState === WebSocket.OPEN) {
      tradingWsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const sendCharts = useCallback((msg) => {
    if (chartsWsRef.current?.readyState === WebSocket.OPEN) {
      chartsWsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const addTradingListener = useCallback((fn) => {
    tradingListenersRef.current.add(fn);
    return () => tradingListenersRef.current.delete(fn);
  }, []);

  const addChartsListener = useCallback((fn) => {
    chartsListenersRef.current.add(fn);
    return () => chartsListenersRef.current.delete(fn);
  }, []);

  const value = {
    tradingReady,
    chartsReady,
    connectionLost,
    sendTrading,
    sendCharts,
    addTradingListener,
    addChartsListener,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    return {
      tradingReady: false,
      chartsReady: false,
      connectionLost: false,
      sendTrading: () => {},
      sendCharts: () => {},
      addTradingListener: () => () => {},
      addChartsListener: () => () => {},
    };
  }
  return ctx;
}
